import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getExam, submitExamResult, calculateScore } from '../../services/exam';

const ExamTaking = () => {
  const { examId } = useParams();
  const { profile, getUserLevel, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [warningShown, setWarningShown] = useState(false);
  const [submittingExam, setSubmittingExam] = useState(false);
  
  const timerRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Check subscription and load exam
  useEffect(() => {
    const loadExam = async () => {
      console.log('🚀 Loading exam:', examId);
      
      if (!hasActiveSubscription()) {
        console.log('🚫 No active subscription, redirecting to dashboard');
        navigate('/dashboard');
        return;
      }
      
      console.log('💳 User has active subscription');

      try {
        const examData = await getExam(examId);
        console.log('📋 Exam data loaded:', examData);
        
        // Verify user has access to this exam's level
        const userLevel = getUserLevel();
        console.log('👤 User level:', userLevel, 'Exam level:', examData.levelId);
        
        if (userLevel !== 'all' && examData.levelId !== userLevel) {
          console.log('🚫 Access denied: User level', userLevel, 'does not match exam level', examData.levelId);
          navigate('/exams');
          return;
        }
        
        console.log('✅ Access granted, setting up exam');
        setExam(examData);
        setTimeRemaining(examData.duration * 60); // Convert minutes to seconds
        setAnswers(new Array(examData.questions.length).fill(null));
      } catch (error) {
        console.error('💥 Error loading exam:', error);
        navigate('/exams');
      }
    };

    loadExam();
  }, [examId, hasActiveSubscription, getUserLevel, navigate]);

  // Full-screen management
  const requestFullScreen = useCallback(() => {
    const elem = containerRef.current;
    if (elem && elem.requestFullscreen) {
      elem.requestFullscreen().catch(err => {
        console.log('Fullscreen request failed:', err);
      });
    } else if (elem && elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem && elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }, []);

  const exitFullScreen = useCallback(() => {
    try {
      // Check if document is still active and not in the process of unloading
      if (document.visibilityState === 'visible' && !document.hidden) {
        // Check if we're actually in fullscreen mode
        if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen().catch(err => {
              console.log('Fullscreen exit failed (expected during navigation):', err);
            });
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen().catch(err => {
              console.log('Webkit fullscreen exit failed (expected during navigation):', err);
            });
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen().catch(err => {
              console.log('MS fullscreen exit failed (expected during navigation):', err);
            });
          }
        }
      }
    } catch (error) {
      // Silently handle any errors during navigation
      console.log('Fullscreen exit handled gracefully:', error.message);
    }
  }, []);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Handle exit fullscreen warning
  useEffect(() => {
    if (examStarted && !examCompleted && !isFullScreen && !warningShown) {
      warningTimeoutRef.current = setTimeout(() => {
        alert('Warning: Exiting fullscreen mode may result in exam submission. Please stay in fullscreen mode.');
        setWarningShown(true);
      }, 5000);
    }

    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [examStarted, examCompleted, isFullScreen, warningShown]);

  const handleSubmitExam = useCallback(async () => {
    if (examCompleted || submittingExam) return;
    
    // Safety check - ensure exam is loaded
    if (!exam) {
      console.error('❌ Exam data not loaded yet');
      return;
    }

    setExamCompleted(true);
    setSubmittingExam(true);
    
    try {
      console.log('🚀 Submitting exam...');
      console.log('📊 Exam data:', {
        examId,
        totalQuestions: exam.questions.length,
        answeredQuestions: answers.filter(a => a !== null).length
      });
      
      const score = await calculateScore(answers, exam);
      const percentage = (score.score / exam.totalMarks) * 100;
      const passed = percentage >= exam.passingMarks;
      
      console.log('📈 Score calculation:', {
        score: score.score,
        totalMarks: exam.totalMarks,
        percentage,
        passingMarks: exam.passingMarks,
        passed
      });
      
      const resultData = {
        examId: exam.id,
        userId: profile.uid,
        levelId: exam.levelId,
        subjectId: exam.subjectId,
        score: score.score,
        totalMarks: exam.totalMarks,
        percentage: percentage,
        passed: passed,
        timeTaken: Math.round((exam.duration * 60 - timeRemaining) / 60), // Convert seconds to minutes
        answers: score.answers,  // Use processed answers with isCorrect property
        startedAt: startTime
      };
      
      console.log('💾 Result data to save:', resultData);

      const result = await submitExamResult(resultData);
      console.log('✅ Exam submitted successfully:', result);
      
      // Exit fullscreen and navigate to results
      exitFullScreen();
      navigate(`/exam/results/${result.id}`);
    } catch (error) {
      console.error('💥 Error submitting exam:', error);
      alert('Failed to submit exam. Please try again.');
      // Reset states on error so user can try again
      setExamCompleted(false);
      setSubmittingExam(false);
    }
  }, [examCompleted, submittingExam, exam, profile, startTime, answers, timeRemaining, navigate]);

  // Timer logic
  useEffect(() => {
    if (examStarted && !examCompleted && timeRemaining > 0 && exam) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0 && !examCompleted && exam) {
      handleSubmitExam();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timeRemaining, examStarted, examCompleted, exam, handleSubmitExam]);

  // Prevent navigation during exam
  useEffect(() => {
    if (exam && profile && examStarted && !examCompleted) {
      // Prevent going back
      const handlePopState = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.history.pushState(null, '', window.location.pathname);
      };

      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [exam, profile, examStarted, examCompleted]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startExam = () => {
    setExamStarted(true);
    setStartTime(new Date());
    requestFullScreen();
  };

  const handleAnswerChange = (answer) => {
    console.log('📝 Answer changed:', {
      questionIndex: currentQuestion,
      newAnswer: answer,
      answerType: typeof answer,
      currentAnswers: answers.map((a, i) => ({ index: i, answer: a?.answer }))
    });
    
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = {
      answer,
      timeSpent: newAnswers[currentQuestion]?.timeSpent || 0
    };
    setAnswers(newAnswers);
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const goToNext = () => {
    if (currentQuestion < exam.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const goToPrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const getAnswerStatus = (index) => {
    if (answers[index] === null) return 'unanswered';
    return 'answered';
  };

  if (!exam) {
    return (
      <div className="exam-loading">
        <div className="loading-container">
          <div className="loading-spinner">Loading exam...</div>
        </div>
      </div>
    );
  }

  if (!examStarted) {
    return (
      <div className="exam-start">
        <div className="container">
          <div className="exam-instructions">
            <h1>{exam.title}</h1>
            <div className="exam-details">
              <div className="detail-item">
                <label>Duration:</label>
                <span>{exam.duration} minutes</span>
              </div>
              <div className="detail-item">
                <label>Total Questions:</label>
                <span>{exam.questions.length}</span>
              </div>
              <div className="detail-item">
                <label>Total Marks:</label>
                <span>{exam.totalMarks}</span>
              </div>
              <div className="detail-item">
                <label>Passing Marks:</label>
                <span>{exam.passingMarks}</span>
              </div>
            </div>
            
            <div className="instructions">
              <h3>Exam Instructions</h3>
              <ul>
                <li>This exam will be conducted in fullscreen mode</li>
                <li>You cannot navigate away from this page during the exam</li>
                <li>The exam will auto-submit when time expires</li>
                <li>Make sure you have a stable internet connection</li>
                <li>Read each question carefully before answering</li>
              </ul>
            </div>
            
            <div className="exam-warnings">
              <h3>⚠️ Important</h3>
              <p>Once you start the exam, you cannot pause or restart. Make sure you're ready before beginning.</p>
            </div>
            
            <button onClick={startExam} className="btn btn-primary btn-large">
              Start Exam
            </button>
          </div>
        </div>
      </div>
    );
  }

  const question = exam.questions[currentQuestion];

  return (
    <div className="exam-taking" ref={containerRef}>
      {/* Submit Loading Overlay */}
      {submittingExam && (
        <div className="submit-loading-overlay">
          <div className="submit-loading-content">
            <div className="loading-spinner-large"></div>
            <h3>Submitting Exam...</h3>
            <p>Please wait while we calculate your results.</p>
          </div>
        </div>
      )}
      
      <div className="exam-header">
        <div className="exam-info">
          <h2>{exam.title}</h2>
          <div className="timer">
            <span className={`time-remaining ${timeRemaining < 300 ? 'warning' : ''}`}>
              ⏱️ {formatTime(timeRemaining)}
            </span>
          </div>
        </div>
        
        <div className="question-nav">
          <div className="question-indicator">
            Question {currentQuestion + 1} of {exam.questions.length}
          </div>
        </div>
      </div>

      <div className="exam-content">
        <div className="question-sidebar">
          <h4>Question Navigator</h4>
          <div className="question-grid">
            {exam.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => goToQuestion(index)}
                className={`question-btn ${getAnswerStatus(index)} ${index === currentQuestion ? 'current' : ''}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          <div className="legend">
            <div className="legend-item">
              <span className="legend-color answered"></span>
              <span>Answered</span>
            </div>
            <div className="legend-item">
              <span className="legend-color unanswered"></span>
              <span>Not Answered</span>
            </div>
            <div className="legend-item">
              <span className="legend-color current"></span>
              <span>Current</span>
            </div>
          </div>
        </div>

        <div className="question-main">
          <div className="question-card">
            <div className="question-header">
              <span className="question-number">Question {currentQuestion + 1}</span>
              <span className="question-marks">{question.marks} marks</span>
            </div>
            
            <div className="question-text">
              <h3>{question.question}</h3>
            </div>

            <div className="answer-options">
              {question.type === 'multiple_choice' && (
                <div className="multiple-choice">
                  {question.options.map((option, index) => (
                    <label key={index} className="option-label">
                      <input
                        type="radio"
                        name="answer"
                        value={index}
                        checked={answers[currentQuestion]?.answer === index}
                        onChange={() => handleAnswerChange(index)}
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.type === 'true_false' && (
                <div className="true-false">
                  <label className="option-label">
                    <input
                      type="radio"
                      name="answer"
                      value="true"
                      checked={answers[currentQuestion]?.answer === true}
                      onChange={() => handleAnswerChange(true)}
                    />
                    <span className="option-text">True</span>
                  </label>
                  <label className="option-label">
                    <input
                      type="radio"
                      name="answer"
                      value="false"
                      checked={answers[currentQuestion]?.answer === false}
                      onChange={() => handleAnswerChange(false)}
                    />
                    <span className="option-text">False</span>
                  </label>
                </div>
              )}

              {question.type === 'short_answer' && (
                <div className="short-answer">
                  <textarea
                    value={answers[currentQuestion]?.answer || ''}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    placeholder="Type your answer here..."
                    rows={4}
                  />
                </div>
              )}

              {question.type === 'essay' && (
                <div className="essay-answer">
                  <textarea
                    value={answers[currentQuestion]?.answer || ''}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    placeholder="Write your essay answer here..."
                    rows={8}
                  />
                </div>
              )}

              {question.type === 'fill_blank' && (
                <div className="fill-blank">
                  {question.text.split('___').map((part, index, array) => (
                    <span key={index}>
                      {part}
                      {index < array.length - 1 && (
                        <input
                          type="text"
                          value={answers[currentQuestion]?.answer?.[index] || ''}
                          onChange={(e) => {
                            const newAnswer = answers[currentQuestion]?.answer || [];
                            newAnswer[index] = e.target.value;
                            handleAnswerChange(newAnswer);
                          }}
                          placeholder="Answer"
                          className="blank-input"
                        />
                      )}
                    </span>
                  ))}
                </div>
              )}

              {question.type === 'matching' && (
                <div className="matching">
                  <div className="matching-columns">
                    <div className="column">
                      <h4>Column A</h4>
                      {question.columnA.map((item, index) => (
                        <div key={index} className="matching-item">{item}</div>
                      ))}
                    </div>
                    <div className="column">
                      <h4>Column B</h4>
                      {question.columnB.map((item, index) => (
                        <label key={index} className="matching-item">
                          <input
                            type="radio"
                            name="matching-answer"
                            value={index}
                            checked={answers[currentQuestion]?.answer === index}
                            onChange={() => handleAnswerChange(index)}
                          />
                          <span>{item}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {question.imageUrl && (
                <div className="question-image">
                  <img src={question.imageUrl} alt="" />
                </div>
              )}
            </div>
          </div>

          <div className="question-navigation">
            <button
              onClick={goToPrevious}
              className="btn btn-secondary"
              disabled={currentQuestion === 0}
            >
              Previous
            </button>
            
            <div className="nav-info">
              {currentQuestion + 1} / {exam.questions.length}
            </div>
            
            {currentQuestion === exam.questions.length - 1 ? (
              <button
                onClick={handleSubmitExam}
                className="btn btn-primary"
                disabled={submittingExam || examCompleted}
              >
                {submittingExam ? (
                  <>
                    <span className="loading-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Exam'
                )}
              </button>
            ) : (
              <button
                onClick={goToNext}
                className="btn btn-primary"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamTaking;
